import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, FormControl, FormHelperText, MenuItem, Select, TextField, useTheme } from "@mui/material";
import { styled } from "@mui/material";
import { Ico_Warning, useSnackbar } from "@sagecentral/core-ui";
import useWorkItemDraftContext from "hooks/work-items/useWorkItemDraftContext";
import useWorkItemDraftCreate from "hooks/work-items/useWorkItemDraftCreate";
import useWorkItemDraftFieldUpdater from "hooks/work-items/useWorkItemDraftFieldUpdater";
import useWorkItemDraftOptions from "hooks/work-items/useWorkItemDraftOptions";
import { IWorkItemDraft } from "models/work-items";
import IStepItem, { IFieldError } from "models/work-items/IStepItem";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { WorkitemCreateStepper } from ".";

type WorkItemCreateBasicInfoProps = {
}

const FormTitle = styled(Box)({
    fontSize: '22px',
    marginBottom: '10px',
    fontWeight: 'bold'
});

const FormRow = styled(Box)({
    display: "flex",
    width: '100%',
    marginBottom: '5px'
});

const FormLabel = styled(Box)({
    width: '250px',
    display: 'flex',
    alignItems: 'center'
});

const FormField = styled(Box)({
    width: '350px'
});

const FormTextField = styled(TextField)(({ theme }) => ({
    'label + &': {
        marginTop: theme.spacing(3),
    },
    '& .MuiOutlinedInput-root': {
        height: '36px',
        innerHeight: '36px',
    },
    '& .MuiTextField-root': {
        height: '43px',
        borderRadius: 4,
        position: 'relative',
        //backgroundColor: theme.palette.mode === 'light' ? '#FFFFFF' : '#FFFFFF',
        border: '1px solid',
        //borderColor: theme.palette.mode === 'light' ? '#E0E3E7' : '#2D3843',
        fontSize: 16,
        width: '100%',
        padding: '10px 12px',
        transition: theme.transitions.create([
            'border-color',
            'background-color',
            'box-shadow',
        ]),
        '&:focus': {
            //boxShadow: `${alpha(theme.palette.primary.main, 0.25)} 0 0 0 0.2rem`,
            //borderColor: theme.palette.primary.main,
        },
    },
}));

const getFieldError = (field: string, step: IStepItem | null) => {
    if (!step) return null;
    if (!step.fieldErrors) return null;

    const errors = step.fieldErrors[field] as IFieldError[] | undefined;
    if ((errors?.length ?? 0) == 0) {
        return null;
    }

    return errors?.filter(e => e.type !== 'info').at(0) ?? null;
};

const getErrorMap = (step: IStepItem | null) => {
    const map = new Map<string, IFieldError | null>();
    map.set('name', getFieldError('name', step));
    map.set('description', getFieldError('description', step));
    map.set('status', getFieldError('status', step));
    map.set('workspace', getFieldError('workspace', step));
    map.set('template', getFieldError('template', step));
    map.set('contentType', getFieldError('contentType', step));
    map.set('financialDivision', getFieldError('financialDivision', step));
    map.set('company', getFieldError('company', step));
    return map;
};

const isError = (error?: IFieldError | null) => {
    return !!error && error.type === 'error';
};

const isWarning = (error?: IFieldError | null) => {
    return !!error && error.type === 'warn';
}

const getColor: (error?: IFieldError | null) => "error" | "info" | "warning" | undefined = (error?: IFieldError | null) => {
    if (!!error) {
        if (error.type === 'warn')
            return 'warning';
        if (error.type === 'error')
            return 'error';
    }

    return 'info';
}

const ErrorHelperText = (props: { field: string, errors: Map<string, IFieldError | null> }) => {
    const { field, errors } = props;
    const error = errors.get(field);
    const theme = useTheme();

    return (
        <FormHelperText
            sx={{
                display: 'flex',
                alignItems: 'center'
            }}
            error={isError(error)}
        >
            {isWarning(error) && <><Ico_Warning htmlColor={theme.palette.warning.dark} sx={{ width: 20, height: 20 }} />&nbsp;</>}
            {error?.message}
        </FormHelperText>
    )
};

const WorkItemCreateBasicInfo: React.FC<WorkItemCreateBasicInfoProps> = () => {
    let navigate = useNavigate();
    const { workItem, options, activeStep, revalidate } = useWorkItemDraftContext();
    const [workspaceOptions, loadingWorkspaceOptions] = useWorkItemDraftOptions(workItem?.workspaceId ?? 0);
    const [name, setNameValue] = useState<string | null>(workItem?.name ?? null);
    const [description, setDescriptionValue] = useState<string | null>(workItem?.description ?? null);
    const [status, setStatusValue] = useState<number | null>(workItem?.statusId ?? 2);
    const [workspaceId, setWorkspaceId] = useState<number | null>(workItem?.workspaceId ?? null);
    const [template, setTemplateValue] = useState<number | null>(workItem?.templateId ?? null);
    const [contentType, setContentTypeValue] = useState<number | null>(workItem?.contentTypeId ?? null);
    const [financialDivision, setFinancialDivisionValue] = useState<number | null>(workItem?.financialDivisionId ?? null);
    const [company, setCompanyValue] = useState<number | null>(workItem?.companyId ?? null);
    const [pendingTemplate, setPendingTemplate] = useState<number | null>(null);
    const [pendingWorkspace, setPendingWorkspace] = useState<number | null>(null);
    const [openDialog, setOpenDialog] = useState(false);

    useEffect(() => { 
        if(workItem?.workspaceId)
            setWorkspaceId(workItem?.workspaceId)
    },[workItem?.workspaceId]);

    const { displaySnackbar } = useSnackbar();
    const handleCreationSuccess = () => {
        displaySnackbar('success', `Work item draft has been created`);
    };
    const handleFieldSuccess = (message: string) => {
        displaySnackbar('success', `Change has been successful. ${message}`);
        revalidate();
    };

    const handleWorkspaceChange = (selectedWorkspaceId: number) => {
        if ((workItem?.workspaceId && workItem?.workspaceId !== selectedWorkspaceId 
            && workItem?.workspaceId > 0) || (workspaceId && workspaceId > 0)) {
            setPendingWorkspace(selectedWorkspaceId);
            setOpenDialog(true);
        } else {
            setWorkspaceId(selectedWorkspaceId);
            setRelatedWorkspace(selectedWorkspaceId);
            setTemplateValue(null);
            setContentTypeValue(null);
        }
    };
    

    const updateUrlWithId = (resultId: string) => {
        navigate(`/workitems/create/${resultId}/basic-info`, { replace: true });
    };

    const [createDraft] = useWorkItemDraftCreate({
        onCreate: (result: IWorkItemDraft) => {
            handleCreationSuccess();
            updateUrlWithId(result?.id.toString());
        }
    });

    const [setName] = useWorkItemDraftFieldUpdater('name', null, {
        onUpdate: () => handleFieldSuccess('Name has been updated.')
    });
    const [setDescription] = useWorkItemDraftFieldUpdater('description', null, {
        onUpdate: () => handleFieldSuccess('Description has been updated.')
    });
    const [setStatus] = useWorkItemDraftFieldUpdater('status', null, {
        onUpdate: () => handleFieldSuccess('Status has been updated.')
    });
    const [setRelatedWorkspace] = useWorkItemDraftFieldUpdater('workspace', null, {
        onUpdate: () => {
            handleFieldSuccess('Workspace has been updated.');
            setTemplate(0);
            setContentType(0);
        }
    });
    const [setTemplate] = useWorkItemDraftFieldUpdater('template', null, {
        onUpdate: (result: IWorkItemDraft) => {
            if (result.templateId && result.templateId > 0)
                handleFieldSuccess('Template has been updated.')
        }
    });
    const [setContentType] = useWorkItemDraftFieldUpdater('content-type', null, {
        onUpdate: (result: IWorkItemDraft) => {
            if (result.contentTypeId && result.contentTypeId > 0)
                handleFieldSuccess('Content type has been updated.')
        }
    });
    const [setFinancialDivision] = useWorkItemDraftFieldUpdater('financial-division', null, {
        onUpdate: () => handleFieldSuccess('Financial division has been updated.')
    });
    const [setCompany] = useWorkItemDraftFieldUpdater('company', null, {
        onUpdate: () => handleFieldSuccess('Company has been updated.')
    });

    const errors = getErrorMap(activeStep);

    const getErrorProps = (field: string) => {
        const error = errors.get(field);
        return {
            error: isError(error),
            color: getColor(error)
        };
    };

    const handleChangeTemplate = (newTemplate: number) => {
        if ((workItem?.templateId && workItem?.templateId > 0 &&
            workItem?.templateId != newTemplate) || (template && template > 0)) {
            setPendingTemplate(newTemplate);
            setOpenDialog(true);
        } else {
            setTemplateValue(newTemplate);
            setTemplate(newTemplate.toString());
        }
    };    

    const handleConfirmChange = () => {
        if (pendingWorkspace !== null) {
            setWorkspaceId(pendingWorkspace);
            setRelatedWorkspace(pendingWorkspace);
            setTemplateValue(null);
            setContentTypeValue(null);
        }
        if (pendingTemplate !== null) {
            setTemplateValue(pendingTemplate);
            setTemplate(pendingTemplate.toString());
        }
        setOpenDialog(false);
        setPendingWorkspace(null);
        setPendingTemplate(null);
    };
    

    const handleCancelChange = () => {
        setPendingTemplate(null);
        setPendingWorkspace(null);
        setOpenDialog(false);
    };


    return (
        <Box>
            {(options == null) && <CircularProgress sx={{ display: 'flex', alignSelf: 'center' }} />
                ||
                <>
                    <FormTitle>
                        Basic Information
                    </FormTitle>
                    <FormRow>
                        <FormLabel>Work Item Name&nbsp; <Box sx={{color: workItem?.name ? 'black' : '#dc3545' }}><i>(required)</i></Box></FormLabel>
                        <FormField sx={{ maxWidth: '60%', flex: 1 }}>
                            <FormControl fullWidth
                            >
                                <FormTextField {...getErrorProps('name')}
                                    required
                                    value={name ?? workItem?.name}
                                    onChange={(evt) => setNameValue(evt.target.value)}
                                    onBlur={(evt) => {
                                        const value = evt.target.value.trim();

                                            if (activeStep) {
                                                const currentErrors = activeStep.fieldErrors?.name ?? [];

                                                const filteredErrors = currentErrors.filter(
                                                    ( err: { message: string; }) => err.message !== "Allows maximum 255 characters"
                                                );

                                                if (value.length > 255) {
                                                    if (!activeStep.fieldErrors) {
                                                        activeStep.fieldErrors = {};
                                                    }

                                                    activeStep.fieldErrors["name"] = [
                                                        ...filteredErrors,
                                                        { message: "Allows maximum 255 characters", type: "error" }
                                                    ];
                                                } else {
                                                    if (filteredErrors.length > 0) {
                                                        activeStep.fieldErrors["name"] = filteredErrors;
                                                    } else if(activeStep.fieldErrors) {
                                                            delete activeStep.fieldErrors["name"];
                                                    }
                                                }
                                            }

                                            if (value !== workItem?.name && value.length > 0 && value.length <= 255) {
                                                workItem === null ? createDraft(value) : setName(value);
                                            } else {
                                                setNameValue(workItem?.name ?? null);
                                            }
                                        }}
                                    />
                                <ErrorHelperText field='name' errors={errors} />
                            </FormControl>
                        </FormField>
                    </FormRow>
                    <FormRow>
                        <FormLabel>Description</FormLabel>
                        <FormField sx={{ maxWidth: '60%', flex: 1 }}>
                            <FormControl fullWidth>
                                <FormTextField {...getErrorProps('description')}
                                    value={description ?? workItem?.description}
                                    onChange={(evt) => setDescriptionValue(evt.target.value)}
                                    onBlur={(evt) => {
                                        const value = evt.target.value.trim();

                                        if (activeStep) {
                                            const currentErrors = activeStep.fieldErrors?.description ?? [];

                                            const filteredErrors = currentErrors.filter(
                                                (err: { message: string }) => err.message !== "Allows maximum 300 characters"
                                            );

                                            if (value.length > 300) {
                                                if (!activeStep.fieldErrors) {
                                                    activeStep.fieldErrors = {};
                                                }

                                                activeStep.fieldErrors["description"] = [
                                                    ...filteredErrors,
                                                    { message: "Allows maximum 300 characters", type: "error" }
                                                ];
                                            } else {
                                                if (filteredErrors.length > 0) {
                                                    activeStep.fieldErrors["description"] = filteredErrors;
                                                } else if (activeStep.fieldErrors) {
                                                    delete activeStep.fieldErrors["description"];
                                                }
                                            }
                                        }

                                        if (value !== workItem?.description && value.length <= 300) {
                                            setDescription(value);
                                        } else {
                                            setDescriptionValue(workItem?.description ?? null);
                                        }
                                    }}

                                    disabled={!workItem?.id}
                                />
                            </FormControl>
                            <ErrorHelperText field='description' errors={errors} />
                        </FormField>
                    </FormRow>

                    <FormRow>
                        <FormLabel>Status&nbsp;<Box sx={{color: workItem?.statusId ? 'black' : '#dc3545' }}><i>(required)</i></Box></FormLabel>
                        <FormField>
                            <FormControl fullWidth>
                                <Select {...getErrorProps('status')}
                                    required
                                    id="select-status"
                                    disabled={!options?.statuses || !workItem?.id}
                                    value={status ?? workItem?.statusId}
                                    onChange={(e) => {
                                        setStatusValue(e.target.value as number);
                                        setStatus(e.target.value as number)
                                    }}
                                    displayEmpty={!workItem?.statusId}>
                                    {options?.statuses.map((value, index) => (
                                        <MenuItem key={index}
                                            value={value.id}>
                                            {value.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                                <ErrorHelperText field='status' errors={errors} />
                            </FormControl>
                        </FormField>
                    </FormRow>

                    <FormRow>
                        <FormLabel>Workspace&nbsp;<Box sx={{color: workspaceId ? 'black' : '#dc3545' }}><i>(required)</i></Box></FormLabel>
                        <FormField>
                            <FormControl fullWidth>
                                <Select {...getErrorProps('workspace')}
                                    required
                                    id="select-related-workspace"
                                    disabled={!options?.workspaces || !workItem?.id}
                                    value={workspaceId} 
                                    onChange={(e) => handleWorkspaceChange(e.target.value as number)}
                                    displayEmpty={!workItem?.workspaceId}>
                                    {options?.workspaces?.map((value, index) => (
                                        <MenuItem key={index}
                                            value={value.id}>
                                            {value.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                                <ErrorHelperText field='workspace' errors={errors} />
                            </FormControl>
                        </FormField>
                    </FormRow>

                    <FormRow>
                        <FormLabel>Work Item Template&nbsp;<Box sx={{color: workItem?.templateId ? 'black' : '#dc3545' }}><i>(required)</i></Box></FormLabel>
                        <FormField>
                            <FormControl fullWidth>
                                {loadingWorkspaceOptions &&
                                    <CircularProgress /> || <>
                                        <Select {...getErrorProps('template')}
                                            required
                                            id="select-work-item-template"
                                            disabled={!workspaceOptions?.templates}
                                            value={template ?? workItem?.templateId}
                                            onChange={(e) => handleChangeTemplate(e.target.value as number)}
                                            displayEmpty={!workItem?.workspaceId && !workspaceId}>
                                            {workspaceOptions?.templates?.map((value, index) => (
                                                <MenuItem key={index}
                                                    value={value.id}>
                                                    {value.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        <ErrorHelperText field='template' errors={errors} />
                                    </>
                                }
                            </FormControl>
                        </FormField>
                    </FormRow>

                    <FormRow>
                        <FormLabel>Content Type&nbsp;<Box sx={{color: workItem?.contentTypeId ? 'black' : '#dc3545' }}><i>(required)</i></Box></FormLabel>
                        <FormField>
                            <FormControl fullWidth>
                                {loadingWorkspaceOptions &&
                                    <CircularProgress /> || <>
                                        <Select {...getErrorProps('contentType')}
                                            required
                                            id="select-content-type"
                                            disabled={!workItem?.workspaceId && !workspaceId}
                                            value={contentType ?? workItem?.contentTypeId}
                                            onChange={(e) => {
                                                setContentTypeValue(e.target.value as number);
                                                setContentType(e.target.value as string);
                                            }}
                                            displayEmpty={!workItem?.contentTypeId}>

                                            {workspaceOptions?.contentTypes?.map((value, index) => (
                                                <MenuItem key={index}
                                                    value={value.id}>
                                                    {value.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        <ErrorHelperText field='contentType' errors={errors} />
                                    </>
                                }
                            </FormControl>
                        </FormField>
                    </FormRow>

                    <FormRow>
                        <FormLabel>Financial Division&nbsp;<Box sx={{color: workItem?.financialDivisionId ? 'black' : '#dc3545' }}><i>(required)</i></Box></FormLabel>
                        <FormField>
                            <FormControl fullWidth>
                                <Select {...getErrorProps('financialDivision')}
                                    required
                                    id="select-financial-division"
                                    disabled={!options?.financialDivisions || !workItem?.id}
                                    value={financialDivision ?? workItem?.financialDivisionId}
                                    onChange={(e) => {
                                        setFinancialDivisionValue(e.target.value as number);
                                        setFinancialDivision(e.target.value as string);
                                    }}
                                    displayEmpty={!workItem?.financialDivisionId}>

                                    {options?.financialDivisions?.map((value, index) => (
                                        <MenuItem key={index}
                                            value={value.id}>
                                            {value.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                                <ErrorHelperText field='financialDivision' errors={errors} />
                            </FormControl>
                        </FormField>
                    </FormRow>

                    <FormRow>
                        <FormLabel>Company&nbsp;<Box sx={{color: workItem?.companyId ? 'black' : '#dc3545' }}><i>(required)</i></Box></FormLabel>
                        <FormField>
                            <FormControl fullWidth>
                                <Select {...getErrorProps('company')}
                                    required
                                    id="select-company"
                                    value={workItem?.companyId}
                                    disabled={!options?.companies || !workItem?.id}
                                    onChange={(e) => {
                                        setCompanyValue(e.target.value as number);
                                        setCompany(e.target.value as string);
                                    }}
                                    displayEmpty>

                                    {options?.companies?.map((value, index) => (
                                        <MenuItem key={index}
                                            value={value.id}>
                                            {value.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                                <ErrorHelperText field='company' errors={errors} />
                            </FormControl>
                        </FormField>
                    </FormRow>
                    <Dialog open={openDialog} onClose={handleCancelChange}>
                        <DialogTitle sx={{ textAlign: 'left', fontWeight: '600', fontSize: '17px' }}>
                            <Ico_Warning color='error' sx={{ height: '40px', width: '40px' }} />
                            <br />
                            Configuration Change
                        </DialogTitle>
                        <DialogContent>
                            This change will update all configurations. Do you want to continue?
                        </DialogContent>
                        <DialogActions>
                            <Button fullWidth onClick={handleCancelChange} variant="outlined">
                                Cancel
                            </Button>
                            <Button fullWidth onClick={handleConfirmChange} variant='contained'>
                                Update
                            </Button>
                        </DialogActions>
                    </Dialog>
                </>
            }
        </Box>
    );
}

export default WorkItemCreateBasicInfo;
